import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useGalleryContext } from 'components/common/gallery';
import { identitySelector } from 'components/common/gallery/selectors';
import { useDocumentationForm } from 'components/dataCreationForms/withDocumentationForm';
import { usePinnedWhileOpen } from 'hooks/usePinnedWhileOpen';
import { DocumentationFile } from 'store/documentation/model/types';
import { getGalleryFileKey } from 'store/documentation/slices/gallery/model';
import { ReactElement, useCallback } from 'react';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';

import { DocumentationEdit } from '.';

const dialogSx = {
  '& .MuiDialog-paper': {
    margin: 2,
    width: '100%',
  },
} satisfies SxProps<Theme>;

const dialogTitleSx = {
  padding: 2,
  '& .MuiTypography-root': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '18px',
  },
} satisfies SxProps<Theme>;

const dialogContentSx = { padding: 0 } satisfies SxProps<Theme>;

function _DocumentationEditDialog(): ReactElement {
  const { useMainDisplayedFile, useDisplayedFiles } =
    useGalleryContext<DocumentationFile>();
  const [mainDisplayedFile] = useMainDisplayedFile(identitySelector);
  const [files] = useDisplayedFiles(identitySelector);
  const { isOpen, closeDialog } = useDocumentationForm();

  // Grid refreshes (e.g. search results being replaced) can move the current
  // slide onto another file while the dialog is open - keep editing the file
  // the dialog was opened for.
  const editedFile = usePinnedWhileOpen(
    isOpen,
    mainDisplayedFile,
    files,
    getGalleryFileKey
  );
  // Backdrop click and Escape (the only close reasons) both close the dialog.
  const handleClose: DialogProps['onClose'] = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  // The gallery's EditComponent contract requires an element.
  if (!editedFile) {
    return <></>;
  }

  return (
    <Dialog
      sx={dialogSx}
      open={isOpen}
      keepMounted={false}
      onClose={handleClose}
      data-qa='documentation-edit-form-dialog'
    >
      <DialogTitle sx={dialogTitleSx}>
        <FormattedMessage id={CommonTranslationKeys.editDocumentation} />
      </DialogTitle>
      <Divider />
      <DialogContent sx={dialogContentSx}>
        <DocumentationEdit file={editedFile} close={closeDialog} />
      </DialogContent>
    </Dialog>
  );
}

export const DocumentationEditDialog = _DocumentationEditDialog;
